/**
 * The conclusion, and what still needs a human.
 *
 * "Requiere tu revisión" is the part worth defending: failed controls, documents that disagree,
 * figures the extractor was unsure of, and information the analysis says is missing all used to be
 * in four different places, so spotting them was the analyst's job. Now it is the screen's.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, friendlyError } from "../../api/client";
import { RECOMMENDATIONS, label } from "../../lib/labels";
import {
  attentionItems,
  coverageLabel,
  gradeChip,
  evidenceCaption,
  evidenceIsExternal,
  findingsByCategory,
  isRunning,
  reportDownloads,
  reportLabel,
  severityLabel,
  verdict,
} from "../../lib/model";
import type { AssessmentOut, CaseOut } from "../../api/types";
import type { EvidenceRequest } from "../../components/EvidenceDrawer";
import DecisionPanel from "./DecisionPanel";
import { fallbackReportName, saveFile } from "../../lib/download";
import {
  Button,
  Chip,
  EmptyState,
  SectionTitle,
  Spinner,
  Stats,
  VerdictPanel,
} from "../../components/ui";

const TONE_BORDER = {
  good: "border-good",
  warn: "border-warn",
  bad: "border-bad",
  neutral: "border-neutral",
} as const;
const TONE_TEXT = {
  good: "text-good",
  warn: "text-warn",
  bad: "text-bad",
  neutral: "text-neutral",
} as const;

export default function Summary({
  kase,
  onEvidence,
  onOpenScore,
}: {
  kase: CaseOut;
  onEvidence: (request: EvidenceRequest) => void;
  onOpenScore: () => void;
}) {
  const assessment = useQuery({
    queryKey: ["assessment", kase.id],
    queryFn: () => api.getAssessment(kase.id),
    retry: false,
  });
  const profile = useQuery({
    queryKey: ["profile", kase.id],
    queryFn: () => api.getProfile(kase.id),
    retry: false,
  });
  const [showAll, setShowAll] = useState(false);

  if (assessment.isLoading) return <Spinner />;

  if (!assessment.data) {
    if (isRunning(kase)) {
      return (
        <EmptyState
          title="El análisis está en curso"
          text="Cuando termine, acá vas a ver la recomendación, los hallazgos y la evidencia."
        />
      );
    }
    if (!kase.documents.length) {
      return (
        <EmptyState
          title="Este legajo todavía no tiene documentos"
          text="Subí los estados contables, el informe de bureau y el memo en la pestaña «Documentos»."
        />
      );
    }
    return (
      <EmptyState
        title="Todavía no se analizó este legajo"
        text="Usá «Iniciar análisis» para procesarlo."
      />
    );
  }

  const data = assessment.data;
  const result = verdict(data);
  const pending = attentionItems(profile.data, data);
  const shown = showAll ? pending : pending.slice(0, 10);

  return (
    <>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <VerdictPanel label={result.label} meaning={result.meaning} tone={result.tone} />
        <div>
          <Stats
            items={[
              ["Alertas críticas", result.redFlags],
              ["Riesgos", result.risks],
              ["Fortalezas", result.strengths],
              ["Falta información", result.gaps],
            ]}
          />
          <p className="mt-4 text-sm text-muted">{data.disclaimer}</p>
        </div>
      </div>

      <ScoreBand kase={kase} assessment={data} onOpenScore={onOpenScore} />

      <div className="mt-6">
        <DecisionPanel caseId={kase.id} recommendation={kase.recommendation} />
      </div>

      {pending.length ? (
        <>
          <SectionTitle hint="Controles que no pasaron, documentos que no coinciden, cifras de baja confianza e información faltante.">
            Requiere tu revisión · {pending.length}
          </SectionTitle>
          <ul className="space-y-2">
            {shown.map((item, index) => (
              <li key={index} className={`border-l-[3px] py-1 pl-3 ${TONE_BORDER[item.tone]}`}>
                <div
                  className={`text-[0.7rem] font-bold tracking-wider uppercase ${TONE_TEXT[item.tone]}`}
                >
                  {item.kind}
                </div>
                <div className="text-sm">{item.text}</div>
              </li>
            ))}
          </ul>
          {pending.length > shown.length ? (
            <Button className="mt-3" onClick={() => setShowAll(true)}>
              Ver los {pending.length - shown.length} restantes
            </Button>
          ) : null}
        </>
      ) : null}

      <SectionTitle>Resumen ejecutivo</SectionTitle>
      <p className="max-w-4xl text-sm leading-relaxed whitespace-pre-line">{data.summary}</p>

      <SectionTitle>Situación financiera</SectionTitle>
      <p className="max-w-4xl text-sm leading-relaxed whitespace-pre-line">
        {data.financial_situation}
      </p>

      <SectionTitle>Hallazgos</SectionTitle>
      <div className="space-y-6">
        {findingsByCategory(data).map((group) => (
          <div key={group.label}>
            <Chip text={`${group.label} · ${group.findings.length}`} tone={group.tone} />
            <div className="mt-3 space-y-3">
              {group.findings.map((finding) => (
                <details
                  key={finding.id}
                  className="rounded-lg border border-line px-4 py-3 open:bg-canvas"
                >
                  <summary className="cursor-pointer text-sm font-medium">
                    {finding.title}
                    {severityLabel(finding.severity) ? (
                      <span className="ml-2 font-normal text-muted">
                        · severidad {severityLabel(finding.severity)}
                      </span>
                    ) : null}
                  </summary>
                  <p className="mt-2 max-w-4xl text-sm leading-relaxed whitespace-pre-line">
                    {finding.text}
                  </p>
                  {finding.evidence.length ? (
                    <div className="mt-3">
                      <div className="text-[0.7rem] font-semibold tracking-wider text-muted uppercase">
                        Evidencia que lo respalda
                      </div>
                      <ul className="mt-1.5 space-y-1.5">
                        {finding.evidence.map((item) => (
                          <li key={item.id} className="text-sm">
                            <button
                              onClick={() =>
                                onEvidence({ evidenceId: item.id, title: finding.title })
                              }
                              className={`text-left underline decoration-dotted underline-offset-2 hover:decoration-solid ${
                                evidenceIsExternal(item) ? "text-warn" : "text-muted"
                              }`}
                            >
                              {evidenceIsExternal(item) ? "Fuente externa · " : "Legajo · "}
                              {evidenceCaption(item)}
                            </button>
                            {item.quote ? (
                              <blockquote className="mt-1 border-l-2 border-line pl-3 text-ink/75">
                                {item.quote}
                              </blockquote>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </details>
              ))}
            </div>
          </div>
        ))}
      </div>

      <SectionTitle>Fundamento de la recomendación</SectionTitle>
      <p className="max-w-4xl text-sm leading-relaxed whitespace-pre-line">{data.rationale}</p>

      {data.conditions.length ? (
        <>
          <SectionTitle>Condiciones sugeridas</SectionTitle>
          <ul className="max-w-4xl list-disc space-y-1 pl-5 text-sm">
            {data.conditions.map((condition, index) => (
              <li key={index}>{condition}</li>
            ))}
          </ul>
        </>
      ) : null}

      {data.information_gaps.length ? (
        <>
          <SectionTitle>Información que falta en el legajo</SectionTitle>
          <ul className="max-w-4xl list-disc space-y-1 pl-5 text-sm">
            {data.information_gaps.map((gap, index) => (
              <li key={index}>{gap}</li>
            ))}
          </ul>
        </>
      ) : null}

      <ReportBlock caseId={kase.id} cuit={kase.cuit} onEvidence={onEvidence} />
    </>
  );
}

/**
 * La calificación, en una línea, con el camino a la explicación completa.
 *
 * El resumen no es el lugar para desarmar el scorecard, pero sí para que el analista sepa que
 * existe, sobre cuánta información se calculó, y —lo que más importa— cuándo el modelo leyó en el
 * legajo algo que el puntaje no captura. Ese desacuerdo se muestra: taparlo sería fingir una
 * certeza que el sistema no tiene.
 */
function ScoreBand({
  kase,
  assessment,
  onOpenScore,
}: {
  kase: CaseOut;
  assessment: AssessmentOut;
  onOpenScore: () => void;
}) {
  const score = kase.score;
  const grade = gradeChip(score);
  if (!score || !grade) return null;

  return (
    <div className="mt-6 rounded-lg border border-line">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 text-sm">
        <span className={`text-2xl leading-none font-bold ${TONE_TEXT[grade.tone]}`}>
          {score.grade}
        </span>
        <span className="font-medium">{score.grade_label}</span>
        <span className="text-muted">·</span>
        <span className="text-muted">{coverageLabel(score.coverage, score.partial)}</span>
        {score.knockouts ? (
          <>
            <span className="text-muted">·</span>
            <span className="text-bad">
              {score.knockouts} {score.knockouts === 1 ? "tope aplicado" : "topes aplicados"}
            </span>
          </>
        ) : null}
        <button
          onClick={onOpenScore}
          className="ml-auto text-primary underline underline-offset-2"
        >
          Ver cómo se calculó
        </button>
      </div>

      {assessment.score_disagreement ? (
        <div className="border-t border-warn-line bg-warn-bg px-4 py-3">
          <div className="text-[0.7rem] font-bold tracking-wider text-warn uppercase">
            El análisis no coincide con el puntaje
          </div>
          <p className="mt-1 text-sm">{assessment.score_disagreement}</p>
        </div>
      ) : null}

      {assessment.recommendation_disagreement && assessment.model_recommendation ? (
        <div className="border-t border-line px-4 py-3 text-sm text-muted">
          Rige la recomendación de la política del banco. Leyendo el legajo, el análisis habría
          sugerido <strong className="text-ink">{label(RECOMMENDATIONS, assessment.model_recommendation)}</strong>.
        </div>
      ) : null}
    </div>
  );
}

function ReportBlock({
  caseId,
  cuit,
  onEvidence,
}: {
  caseId: string;
  cuit: string;
  onEvidence: (request: EvidenceRequest) => void;
}) {
  const queryClient = useQueryClient();
  const reports = useQuery({ queryKey: ["reports", caseId], queryFn: () => api.listReports(caseId) });
  const [comment, setComment] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!reports.data?.length) return null;
  const { primary, others } = reportDownloads(reports.data);

  async function download(reportId: string, format: string) {
    saveFile(await api.downloadReport(reportId), fallbackReportName(cuit, format));
  }

  async function feedback(rating: "up" | "down") {
    const target = reports.data!.find((r) => r.format === "docx") ?? reports.data![0]!;
    try {
      await api.sendFeedback(target.id, rating, comment.trim() || null);
      setSent(true);
      await queryClient.invalidateQueries({ queryKey: ["reports", caseId] });
    } catch (e) {
      setError(friendlyError(e).message);
    }
  }

  return (
    <>
      <div className="mt-10 border-t border-line pt-6">
        <SectionTitle>Informe</SectionTitle>
        <InlineReportView caseId={caseId} onEvidence={onEvidence} />
        <div className="flex flex-wrap items-center gap-2">
          {primary.map((report) => (
            <Button
              key={report.id}
              variant="primary"
              onClick={() => download(report.id, report.format)}
            >
              Descargar {reportLabel(report.format)}
            </Button>
          ))}
          {others.map((report) => (
            <Button key={report.id} onClick={() => download(report.id, report.format)}>
              {reportLabel(report.format)}
            </Button>
          ))}
        </div>

        <div className="mt-6 max-w-2xl rounded-lg border border-line p-4">
          <div className="text-sm font-medium">¿Te sirvió este borrador?</div>
          {sent ? (
            <p className="mt-2 text-sm text-good">Gracias, quedó registrado.</p>
          ) : (
            <>
              <input
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Comentario (opcional)"
                className="mt-2 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
              <div className="mt-2 flex gap-2">
                <Button onClick={() => feedback("up")}>Sí, sirvió</Button>
                <Button onClick={() => feedback("down")}>No sirvió</Button>
              </div>
            </>
          )}
          {error ? <p className="mt-2 text-sm text-bad">{error}</p> : null}
        </div>
      </div>
    </>
  );
}

/**
 * El informe, leído acá adentro, con sus citas navegables.
 *
 * Antes el único camino era descargar el Word, abrirlo, leer «[Doc 2, p. 5]» y volver a la pantalla
 * a buscar esa página a mano. El DOCX sigue siendo el expediente —se firma, se imprime y se
 * archiva— pero leer no es archivar: acá cada cita es un enlace que abre la página citada con la
 * frase marcada, que es la interacción central del producto.
 *
 * El HTML lo genera esta misma aplicación desde su plantilla, con el texto del modelo ya escapado,
 * y el servidor devuelve sólo el cuerpo. Los clics se escuchan en el contenedor: los enlaces los
 * dibuja el informe, no React.
 */
function InlineReportView({
  caseId,
  onEvidence,
}: {
  caseId: string;
  onEvidence: (request: EvidenceRequest) => void;
}) {
  const [open, setOpen] = useState(false);
  const report = useQuery({
    queryKey: ["inline-report", caseId],
    queryFn: () => api.inlineReport(caseId),
    enabled: open,
    networkMode: "always",
  });

  function onClick(event: React.MouseEvent<HTMLDivElement>) {
    const link = (event.target as HTMLElement).closest("a.cita");
    const evidenceId = link?.getAttribute("data-evidence");
    if (!evidenceId) return;
    event.preventDefault();
    onEvidence({ evidenceId, title: link?.textContent?.trim() || "Cita del informe" });
  }

  return (
    <div className="mt-4">
      <button
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="text-sm text-primary underline underline-offset-2"
      >
        {open ? "Ocultar el informe" : "Leer el informe acá"}
      </button>
      {open ?
        !report.data ?
          <Spinner label="Armando el informe…" />
        : <div
            onClick={onClick}
            className="informe mt-4 max-w-3xl rounded-lg border border-line bg-white px-6 py-5"
            dangerouslySetInnerHTML={{ __html: report.data.html }}
          />

      : null}
    </div>
  );
}
